import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router/composables';
import OAuthConnectionService, { ConsentInfo } from '@/services/OAuthConnectionService';
import notificationService from '@/services/NotificationService';
import store from '@/store';

interface ScopeItem {
  key: string;
  label: string;
  resource: string;
}

interface ScopeGroup {
  resource: string;
  label: string;
  scopes: ScopeItem[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OAuthConsentPage',
  setup(__props) {

const { query } = useRoute();

const loading = ref(true);
const submitting = ref(false);
const error = ref<string | null>(null);
const clientInfo = ref<ConsentInfo | null>(null);

const clientId = computed(() => (query.client_id as string) || '');
const redirectUri = computed(() => (query.redirect_uri as string) || '');
const scope = computed(() => (query.scope as string) || '');
const state = computed(() => (query.state as string) || '');
const codeChallenge = computed(() => (query.code_challenge as string) || '');
const codeChallengeMethod = computed(() => (query.code_challenge_method as string) || 'S256');

const userEmail = computed(() => store.state.user?.email || '');
const userCompanies = computed(() => store.state.userCompanies || []);
const selectedCompanyIds = ref<string[]>(store.state.company?.id ? [store.state.company.id] : []);

const SCOPE_LABELS: Record<string, string> = {
  'companies:read': 'Read company information',
  'articles:read': 'Read articles and their content',
  'workspaces:read': 'Read workspace information',
  'transcriptions:read': 'Read video transcriptions',
};

const RESOURCE_LABELS: Record<string, string> = {
  companies: 'Companies',
  articles: 'Articles',
  workspaces: 'Workspaces',
  transcriptions: 'Transcriptions',
};

const selectedScopes = ref<Set<string>>(new Set());

const scopeCeilingLabels = computed<string[]>(() =>
  (clientInfo.value?.scope_ceiling || []).map((s) => SCOPE_LABELS[s] || s)
);

/** Renders a lifetime sent in seconds by the backend, so the copy follows the server's values. */
const formatDuration = (seconds: number): string => {
  const days = Math.round(seconds / 86400);
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'}`;
  const hours = Math.round(seconds / 3600);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

const lifetimeText = computed<string>(() => {
  const info = clientInfo.value;
  if (!info?.access_token_lifetime || !info.refresh_token_lifetime) return '';
  const cap = info.refresh_token_lifetime_max
    ? `, and in any case after ${formatDuration(info.refresh_token_lifetime_max)}`
    : '';
  return (
    `Access tokens last ${formatDuration(info.access_token_lifetime)}. The app keeps access by refreshing its ` +
    `token. Access ends ${formatDuration(info.refresh_token_lifetime)} after the last refresh${cap}, when you ` +
    'will need to authorize again.'
  );
});

const parsedScopes = computed<ScopeItem[]>(() => {
  const scopes = scope.value.split(' ').filter(Boolean);
  return scopes.map((s) => {
    const resource = s.split(':')[0] || s;
    return { key: s, label: SCOPE_LABELS[s] || s, resource };
  });
});

const groupedScopes = computed<ScopeGroup[]>(() => {
  const groups: Record<string, ScopeItem[]> = {};
  for (const s of parsedScopes.value) {
    if (!groups[s.resource]) groups[s.resource] = [];
    groups[s.resource].push(s);
  }
  return Object.entries(groups).map(([resource, scopes]) => ({
    resource,
    label: RESOURCE_LABELS[resource] || resource,
    scopes,
  }));
});

const hasSelectedScopes = computed(() => selectedScopes.value.size > 0);

const canAuthorize = computed(() => {
  if (!hasSelectedScopes.value) return false;
  if (userCompanies.value.length > 1 && selectedCompanyIds.value.length === 0) return false;
  return true;
});

const allScopesSelected = computed(
  () => parsedScopes.value.length > 0 && parsedScopes.value.every((s) => selectedScopes.value.has(s.key))
);

const allCompaniesSelected = computed(
  () => userCompanies.value.length > 0 && userCompanies.value.every((c: any) => selectedCompanyIds.value.includes(c.id))
);

function isGroupFullySelected(resource: string): boolean {
  const group = groupedScopes.value.find((g) => g.resource === resource);
  if (!group) return false;
  return group.scopes.every((s) => selectedScopes.value.has(s.key));
}

function isGroupPartiallySelected(resource: string): boolean {
  const group = groupedScopes.value.find((g) => g.resource === resource);
  if (!group) return false;
  const selected = group.scopes.filter((s) => selectedScopes.value.has(s.key)).length;
  return selected > 0 && selected < group.scopes.length;
}

function toggleScope(key: string) {
  const newSet = new Set(selectedScopes.value);
  if (newSet.has(key)) {
    newSet.delete(key);
  } else {
    newSet.add(key);
  }
  selectedScopes.value = newSet;
}

function toggleGroupScopes(resource: string) {
  const group = groupedScopes.value.find((g) => g.resource === resource);
  if (!group) return;
  const newSet = new Set(selectedScopes.value);
  const allSelected = group.scopes.every((s) => newSet.has(s.key));
  for (const s of group.scopes) {
    if (allSelected) {
      newSet.delete(s.key);
    } else {
      newSet.add(s.key);
    }
  }
  selectedScopes.value = newSet;
}

function toggleAllScopes() {
  if (allScopesSelected.value) {
    selectedScopes.value = new Set();
  } else {
    selectedScopes.value = new Set(parsedScopes.value.map((s) => s.key));
  }
}

function toggleAllCompanies() {
  if (allCompaniesSelected.value) {
    selectedCompanyIds.value = [];
  } else {
    selectedCompanyIds.value = userCompanies.value.map((c: any) => c.id);
  }
}

const handleAuthorize = async () => {
  submitting.value = true;
  try {
    const result = await OAuthConnectionService.approveConsent({
      client_id: clientId.value,
      redirect_uri: redirectUri.value,
      scope: Array.from(selectedScopes.value).join(' '),
      state: state.value,
      code_challenge: codeChallenge.value,
      code_challenge_method: codeChallengeMethod.value,
      company_ids: selectedCompanyIds.value.length > 0 ? selectedCompanyIds.value : undefined,
    });
    window.location.href = result.redirect_url;
  } catch (err) {
    notificationService.error('Authorization failed. Please try again.');
    submitting.value = false;
  }
};

const handleDeny = async () => {
  submitting.value = true;
  try {
    const result = await OAuthConnectionService.denyConsent({
      client_id: clientId.value,
      redirect_uri: redirectUri.value,
      state: state.value,
    });
    window.location.href = result.redirect_url;
  } catch (err) {
    notificationService.error('Failed to deny authorization.');
    submitting.value = false;
  }
};

onMounted(async () => {
  if (!clientId.value || !redirectUri.value) {
    error.value = 'Invalid authorization request. Missing required parameters.';
    loading.value = false;
    return;
  }

  try {
    clientInfo.value = await OAuthConnectionService.getConsentInfo(clientId.value);
    // Initialize all requested scopes as selected
    const allScopes = scope.value.split(' ').filter(Boolean);
    selectedScopes.value = new Set(allScopes);
  } catch (err) {
    error.value = 'Unknown application. The client could not be found.';
  } finally {
    loading.value = false;
  }
});

return { __sfc: true,query, loading, submitting, error, clientInfo, clientId, redirectUri, scope, state, codeChallenge, codeChallengeMethod, userEmail, userCompanies, selectedCompanyIds, SCOPE_LABELS, RESOURCE_LABELS, selectedScopes, scopeCeilingLabels, formatDuration, lifetimeText, parsedScopes, groupedScopes, hasSelectedScopes, canAuthorize, allScopesSelected, allCompaniesSelected, isGroupFullySelected, isGroupPartiallySelected, toggleScope, toggleGroupScopes, toggleAllScopes, toggleAllCompanies, handleAuthorize, handleDeny }
}

})